import { Flex, Tag, Typography } from 'antd';
import { IconChevronRight, IconTrash, IconUserStar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { RoleCardRole } from '../../types';
import { resolveSystemRoleLabel } from '../../utils';

const { Text } = Typography;

type RoleCardProps = {
  role: RoleCardRole;
  canDeleteRole: boolean;
  onView: (role: RoleCardRole) => void;
  onDelete?: (role: RoleCardRole) => void;
};

export function RoleCard({
  role,
  canDeleteRole,
  onView,
  onDelete,
}: RoleCardProps) {
  const { t } = useTranslation('authorization');
  const label = resolveSystemRoleLabel(role.name, t);
  const showDelete = canDeleteRole && !role.isSystemRole && Boolean(onDelete);

  return (
    <div className="authorization-custom-role-card-wrap">
      <button
        type="button"
        className="authorization-custom-role-card"
        onClick={() => onView(role)}
        aria-label={t('roleCard.viewRole', { name: label })}
      >
        <Flex
          vertical
          className="authorization-custom-role-card-inner"
          gap={12}
        >
          <Flex justify="space-between" align="flex-start" gap={12}>
            <span className="authorization-custom-role-card-icon" aria-hidden>
              <IconUserStar size={22} stroke={1.75} />
            </span>
            <Tag className="authorization-custom-role-card-badge">
              {t('detail.customRoleBadge')}
            </Tag>
          </Flex>

          <Flex
            vertical
            gap={6}
            className="authorization-custom-role-card-body"
          >
            <Text className="authorization-custom-role-card-title">
              {label}
            </Text>
          </Flex>

          <Flex
            vertical
            gap={10}
            className="authorization-custom-role-card-footer"
          >
            <Flex gap={6} wrap className="authorization-custom-role-card-tags">
              {role.permissionCount !== undefined && (
                <Tag className="authorization-custom-role-card-meta-tag">
                  {t('roleCard.permissionCount', {
                    count: role.permissionCount,
                  })}
                </Tag>
              )}
              <Tag className="authorization-custom-role-card-meta-tag">
                {t('roleCard.usersCount', { count: role.usersCount })}
              </Tag>
            </Flex>

            <Flex
              align="center"
              gap={4}
              className="authorization-custom-role-card-action"
            >
              <Text>{t('roleCard.viewDetails')}</Text>
              <IconChevronRight size={16} stroke={2} />
            </Flex>
          </Flex>
        </Flex>
      </button>

      {showDelete && (
        <button
          type="button"
          className="authorization-custom-role-card-delete"
          aria-label={t('rolesTable.delete')}
          onClick={(event) => {
            event.stopPropagation();
            onDelete?.(role);
          }}
        >
          <IconTrash size={16} stroke={1.75} />
        </button>
      )}
    </div>
  );
}
